import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { Rol } from '../api';
import { DialogoDeReingreso, useSesion } from '../sesion';

/**
 * Envoltorio de todo lo que no se puede ver sin sesión.
 *
 * # ESTO ES UX, NO SEGURIDAD
 *
 * Vale la pena decirlo con todas las letras porque es el malentendido más caro
 * posible: **el control de acceso lo hace el backend**, que es deny-by-default
 * (`anyRequest().authenticated()`) y exige `ROLE_ADMIN` en `/api/v1/admin/**`.
 * Quien edite el JavaScript de esta página y se pinte un rol ADMIN va a ver los
 * botones, apretarlos, y comerse un `403` por cada uno. Lo que hacen estos
 * componentes es que la app no le muestre a la gente puertas que no puede abrir
 * y que no se rompa cuando alguien escribe una URL a mano.
 *
 * # LAS TRES SITUACIONES SIN SESIÓN, QUE NO SON LA MISMA
 *
 * 1. **Nunca hubo sesión en esta carga de la página** (entró directo a la URL,
 *    o recargó, que con el token en memoria es lo mismo) → al login, guardando
 *    a dónde quería ir para volver ahí después.
 * 2. **Salió a propósito** → al login también. No hay nada que preservar.
 * 3. **El backend rechazó la credencial con la persona adentro** (`401`) → la
 *    pantalla **se queda montada** y encima va el diálogo de reingreso. Ver
 *    {@link DialogoDeReingreso}: acá es donde no se pierde un formulario a
 *    medio llenar.
 *
 * El rol NO se chequea acá sino en {@link RequiereRol}, que va más adentro. Son
 * dos preguntas distintas —"¿quién sos?" y "¿podés?"— y separarlas evita que
 * una ruta anidada vuelva a resolver la primera y termine con dos diálogos de
 * reingreso encimados.
 */
export function RutaProtegida({ children }: { children: ReactNode }) {
  const { sesion, cierre } = useSesion();
  const ubicacion = useLocation();

  // El email de quien estaba adentro, para prellenar el reingreso. Se guarda en
  // un ref y se actualiza en un efecto: durante el render no se muta nada.
  const ultimoEmail = useRef('');
  useEffect(() => {
    if (sesion) {
      ultimoEmail.current = sesion.email;
    }
  }, [sesion]);

  if (sesion) {
    return <>{children}</>;
  }
  if (cierre === 'credencial_rechazada') {
    return (
      <>
        {children}
        <DialogoDeReingreso email={ultimoEmail.current} />
      </>
    );
  }
  return <Navigate to="/login" replace state={{ desde: ubicacion.pathname }} />;
}

/**
 * Exige un rol. Va **dentro** de una {@link RutaProtegida}: da por hecho que la
 * sesión ya está resuelta y solo se ocupa del permiso.
 */
export function RequiereRol({ rol, children }: { rol: Rol; children: ReactNode }) {
  const { sesion } = useSesion();

  if (!sesion) {
    // Sin sesión no hay rol que comparar. Quien envuelve a este componente ya
    // está mostrando el login o el diálogo de reingreso; acá no se decide nada.
    return null;
  }
  if (sesion.rol !== rol) {
    return <SinPermiso rolPedido={rol} />;
  }
  return <>{children}</>;
}

/**
 * Lo que ve quien fuerza por URL una ruta de otro rol. **No es un error de la
 * app**: es una pantalla más, con salida. Si esto tirara una excepción o dejara
 * la app en blanco, cualquiera con el link de administración podría romperle la
 * sesión a un operador sin querer.
 */
function SinPermiso({ rolPedido }: { rolPedido: Rol }) {
  return (
    <main>
      <h1>No tenés permiso</h1>
      <p>Esta pantalla es para el rol {rolPedido} y tu usuario no lo tiene.</p>
      <p>
        <Link to="/">Volver al inicio</Link>
      </p>
    </main>
  );
}
